<template>
  <div class="title-box">
    <div class="title">
      {{ info.title }}
    </div>
  </div>
  <div v-show="info.status === 'edit'">
    <div class="editor-box">
      <div ref="editor" class="editor"/>
    </div>
  </div>
  <div class="ql-snow" v-show="info.status === 'show'">
    <div ref="show"/>
  </div>
</template>

<script lang="ts">
import {definedWidgetMeta} from "@/components/business/crud/widgets/widgetManager";

export const meta = definedWidgetMeta({
  size: {edit: 'large', show: 'large'},
});
</script>

<script lang="ts" setup>
import {defineProps, onMounted, ref} from 'vue';
import {DateFieldType} from '@/components/business/crud/interface/FieldType';
import {WidgetInfo} from "@/components/business/crud/widgets/WidgetContainer"
import {ScForm} from "@/models/ScForm";
import {createQuill} from "@/util/editor";
import Quill from "quill";

const props = defineProps<{
  propName: string;
  widget: DateFieldType;
  info: WidgetInfo;
}>();

const data = ScForm.useFormData();
const editor = ref<HTMLElement>();
const show = ref<HTMLElement>();
onMounted(() => {
  if (editor.value) {
    const quill = createQuill(editor.value, {placeholder: '请编辑' + props.info.title});
    quill.setContents(data.value[props.propName]);
    quill.on("text-change", () => {
      data.value[props.propName] = quill.getContents();
    });
  }
  if (show.value) {
    const quill = new Quill(show.value, {readOnly: true,  modules: {toolbar: "#notoolbar"}});
    quill.setContents(data.value[props.propName]);
    quill.disable()
  }
})
</script>

<style lang="scss" scoped>
.editor-view {
  height: 100%;
  padding-left: 12px;
}

.editor {
  min-height: 400px;
}


.title-box {
  font-size: $font-size;
  padding: 8px 8px;
  border: 1px solid #f0f0f0;
  border-bottom: none;
  display: flex;
  flex-direction: row;
  background: rgb(250, 250, 250);

  .title {
    cursor: pointer;
  }
}

:deep(.editor-box) {
  .ql-container {
    height: 300px;
  }

  .editor-upload {
    display: none;
  }

  /*
    文字大小
  */
  .ql-snow .ql-picker.ql-size {
    width: 50px; // 菜单栏占比宽度
  }

  .ql-snow .ql-picker.ql-size .ql-picker-label::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item::before {
    content: '14';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='10px']::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='10px']::before {
    content: '10';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='12px']::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='12px']::before {
    content: '12';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='14px']::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='14px']::before {
    content: '14';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='16px']::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='16px']::before {
    content: '16';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='18px']::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='18px']::before {
    content: '18';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='20px']::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='20px']::before {
    content: '20';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='24px']::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='24px']::before {
    content: '24';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='26px']::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='26px']::before {
    content: '26';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='32px']::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='32px']::before {
    content: '32';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='48px']::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='48px']::before {
    content: '48';
  }

  /*
    字体
  */
  .ql-snow .ql-picker.ql-font {
    width: 80px; // 菜单栏占比宽度
  }

  .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='宋体']::before,
  .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='宋体']::before {
    content: '宋体';
  }

  .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='黑体']::before,
  .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='黑体']::before {
    content: '黑体';
  }

  .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='微软雅黑']::before,
  .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='微软雅黑']::before {
    content: '微软雅黑';
  }

  .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='楷体']::before,
  .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='楷体']::before {
    content: '楷体';
  }

  .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='仿宋']::before,
  .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='仿宋']::before {
    content: '仿宋';
  }

  .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='Arial']::before,
  .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='Arial']::before {
    content: 'Arial';
  }

  .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='苹方']::before,
  .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='苹方']::before {
    content: '苹方';
  }

  /*
    标题
  */
  .ql-snow .ql-picker.ql-header {
    width: 80px; // 菜单栏占比宽度
  }

  .ql-snow .ql-picker.ql-header .ql-picker-label::before,
  .ql-snow .ql-picker.ql-header .ql-picker-item::before {
    content: "正文" !important;
  }

  .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="1"]::before,
  .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="1"]::before {
    content: "标题1" !important;
  }

  .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="2"]::before,
  .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="2"]::before {
    content: "标题2" !important;
  }

  .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="3"]::before,
  .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="3"]::before {
    content: "标题3" !important;
  }

  .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="4"]::before,
  .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="4"]::before {
    content: "标题4" !important;
  }

  .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="5"]::before,
  .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="5"]::before {
    content: "标题5" !important;
  }

  .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="6"]::before,
  .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="6"]::before {
    content: "标题6" !important;
  }

  //配置编辑器行高
  .ql-snow .ql-picker.ql-lineHeight {
    width: 70px; // 菜单栏占比宽度
  }

  .ql-snow .ql-picker.ql-lineHeight .ql-picker-label::before {
    content: '行高';
  }

  .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='initial']::before {
    content: '默认';
  }

  .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='1']::before {
    content: '1';
  }

  .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='1.5']::before {
    content: '1.5';
  }

  .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='1.75']::before {
    content: '1.75';
  }

  .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='2']::before {
    content: '2';
  }

  .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='3']::before {
    content: '3';
  }

  .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='4']::before {
    content: '4';
  }

  .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='5']::before {
    content: '5';
  }
}
</style>
